import React from 'react';
import { useParallax } from 'react-scroll-parallax';

/*
 * Image assets (all “blue” versions)
 */
const BUBBLES_BLUE =
  'https://santaplaya-bucket.s3.amazonaws.com/bubbles_blue.webp';
const BLUE_TILAPIA =
  'https://santaplaya-bucket.s3.amazonaws.com/blueTalapia.webp';
const BLUE_FISH = 'https://santaplaya-bucket.s3.amazonaws.com/blue_fish.webp';
const BLUE_CRAB = 'https://santaplaya-bucket.s3.amazonaws.com/blueCrab.webp';
const BLUE_STARFISH =
  'https://santaplaya-bucket.s3.amazonaws.com/blueStarfish.webp';
const BLUE_OCTOPUS =
  'https://santaplaya-bucket.s3.amazonaws.com/blue_octopus.webp';

/**
 * UnderwaterAccent:
 * A background-like layer of sea creatures for the Contact Us page.
 * Each image is absolutely positioned and has a subtle parallax effect.
 */
const UnderwaterAccent: React.FC = () => {
  /*
   * We define separate parallax hooks for each image so we can
   * control their motion individually (translateX, translateY, rotate, etc.).
   * Feel free to adjust the numeric arrays for subtler or stronger effects.
   */
  const bubblesLeftRef = useParallax<HTMLImageElement>({
    translateY: [20, -20],
    easing: 'easeInOut',
  });
  const tilapiaRef = useParallax<HTMLImageElement>({
    translateY: [30, -30],
    easing: 'easeInOut',
  });
  const blueFishLeftRef = useParallax<HTMLImageElement>({
    translateX: [15, -15],
    easing: 'easeInOut',
  });
  const crabRef = useParallax<HTMLImageElement>({
    translateX: [-10, 10],
    easing: 'easeInOut',
  });
  const starfishRef = useParallax<HTMLImageElement>({
    translateY: [10, -10],
    easing: 'easeInOut',
  });

  // Right side images
  const blueFishRightRef = useParallax<HTMLImageElement>({
    translateX: [-15, 15],
    easing: 'easeInOut',
  });
  const bubblesRightRef = useParallax<HTMLImageElement>({
    translateY: [20, -20],
    easing: 'easeInOut',
  });
  const octopusRef = useParallax<HTMLImageElement>({
    rotate: [-3, 3],
    easing: 'easeInOut',
  });

  return (
    <div
      className="
        absolute 
        inset-0
        pointer-events-none 
        z-0 
      "
    >
      {/* LEFT-SIDE, top to bottom */}
      <img
        src={BUBBLES_BLUE}
        ref={bubblesLeftRef.ref}
        alt="Bubbles Blue"
        className="absolute top-8 left-[15%] w-[10vw]"
      />

      <img
        src={BLUE_TILAPIA}
        ref={tilapiaRef.ref}
        alt="Blue Tilapia"
        className="absolute top-[12.5%] left-0 w-[24vw]"
      />

      <div
        ref={blueFishLeftRef.ref}
        className="absolute top-[40%] -left-[12.5%] w-[25vw]"
        // Parallax transform is applied to this <div>
      >
        <img
          src={BLUE_FISH}
          alt="Blue Fish Left Flipped"
          className="scale-x-[-1]"
          // The flip transform is on the child <img> only
        />
      </div>

      {/* BOTTOM center-left */}
      <img
        src={BLUE_CRAB}
        ref={crabRef.ref}
        alt="Blue Crab"
        className="absolute bottom-1/2 left-[30%] w-[12vw]"
      />

      {/* BOTTOM center-right */}
      <img
        src={BLUE_STARFISH}
        ref={starfishRef.ref}
        alt="Blue Starfish"
        className="absolute bottom-1/2 right-1/4 w-[12vw]"
      />

      {/* RIGHT-SIDE, bottom to top */}
      <div
        ref={blueFishRightRef.ref}
        className="absolute top-[35%] -right-1/4 w-[35vw]"
        // Parallax transform is applied to this <div>
      >
        <img
          src={BLUE_FISH}
          alt="Blue Fish Right Flipped"
          className="scale-x-[-1]"
          // The flip transform is on the child <img> only
        />
      </div>

      <img
        src={BUBBLES_BLUE}
        ref={bubblesRightRef.ref}
        alt="Bubbles Blue Right"
        className="absolute top-[15%] right-1/4 w-[8vw] "
      />

      <img
        src={BLUE_OCTOPUS}
        ref={octopusRef.ref}
        alt="Blue Octopus"
        className="absolute -top-[10%] -right-[12.5%] w-[35vw]"
      />
    </div>
  );
};

export default UnderwaterAccent;
